import React, { useState, useEffect } from 'react';
import { subscribeToUserCollection, recordPurchase } from '../utils/userDataHelper';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import './labcss/inventory-pages.css';
import useCurrencyTools from '../hooks/useCurrencyTools';

const UNITS = ['kg', 'g', 'L', 'ml', 'pcs'];

const emptyForm = {
  ingredientId: '',
  ingredientName: '',
  supplier: '',
  quantity: '',
  unit: 'kg',
  totalCost: '',
  date: new Date().toISOString().split('T')[0],
  invoiceNo: '',
  notes: '',
};

export default function AddPurchasePage() {
  const [form, setForm] = useState(emptyForm);
  const [ingredients, setIngredients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const { preferredCurrency, currencySymbol, formatMoney } = useCurrencyTools();

  useEffect(() => {
    const unsubs = [
      subscribeToUserCollection('ingredients', setIngredients),
      subscribeToUserCollection('suppliers', setSuppliers),
    ];
    return () => unsubs.forEach(u => u());
  }, []);

  const costPerUnit = form.quantity && form.totalCost
    ? formatMoney(Number(form.totalCost) / Number(form.quantity), preferredCurrency,  { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ingredientId) { alert('Select an existing ingredient before recording a purchase.'); return; }
    setSaving(true);
    try {
      await recordPurchase({
        ingredientId: form.ingredientId,
        ingredientName: form.ingredientName,
        supplier: form.supplier,
        quantity: Number(form.quantity),
        unit: form.unit,
        totalCost: Number(form.totalCost),
        costPerUnit: Number(form.totalCost) / Number(form.quantity),
        date: form.date,
        invoiceNo: form.invoiceNo,
        notes: form.notes,
        currencyCode: preferredCurrency,
      });
      setForm(emptyForm);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving purchase:', err);
      alert('Failed to save purchase: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const supplierNames = [...new Set(suppliers.map(s => s.name).filter(Boolean))];

  return (
    <>
      <div className="sl-page inventory-subpage">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Inventory', to: '/inventory' },
            { label: 'Add Purchase' },
          ]} />
          <div className="pageheader-row">
            <div className="pageheading">Add Purchase</div>
            <div className="pagedescription">Record a new ingredient purchase and track costs.</div>
          </div>
        </div>

        <div className="inventory-subpage-content borderleft">
          {success && (
            <div className="mypadding" style={{ background: '#ecfdf5', color: '#059669', fontWeight: 600 }}>
              Purchase saved and inventory updated!
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <section className="inv-card mypaddingmoretop">
              <h2>Purchase Details</h2>
              <div className="inv-form-grid">
                <div className="inv-field">
                  <label>Ingredient *</label>
                  <select
                    value={form.ingredientId}
                    onChange={e => { const item = ingredients.find(i => i.id === e.target.value); setForm(prev => ({ ...prev, ingredientId: item?.id || '', ingredientName: item?.name || '', unit: item?.unit || 'kg' })); }}
                    required
                  >
                    <option value="">Select inventory ingredient</option>
                    {ingredients.map(item => <option key={item.id} value={item.id}>{item.name} ({item.stock ?? item.stockGrams ?? 0} {item.unit || 'g'})</option>)}
                  </select>
                  <small>Add a new ingredient in Inventory first. Saving increases its stock and updates weighted average cost.</small>
                </div>

                <div className="inv-field">
                  <label>Supplier</label>
                  <input
                    list="supplier-list"
                    value={form.supplier}
                    onChange={e => set('supplier', e.target.value)}
                    placeholder="Select or type supplier"
                  />
                  <datalist id="supplier-list">
                    {supplierNames.map(n => <option key={n} value={n} />)}
                  </datalist>
                </div>

                <div className="inv-field">
                  <label>Quantity *</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={form.quantity}
                      onChange={e => set('quantity', e.target.value)}
                      placeholder="0"
                      required
                      style={{ flex: 1 }}
                    />
                    <select value={form.unit} onChange={e => set('unit', e.target.value)} style={{ width: 80 }}>
                      {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                </div>

                <div className="inv-field">
                  <label>Total Cost ({currencySymbol}) *</label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={form.totalCost}
                    onChange={e => set('totalCost', e.target.value)}
                    placeholder="0.00"
                    required
                  />
                </div>

                <div className="inv-field">
                  <label>Date</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={e => set('date', e.target.value)}
                  />
                </div>

                <div className="inv-field">
                  <label>Invoice / Reference No.</label>
                  <input
                    type="text"
                    value={form.invoiceNo}
                    onChange={e => set('invoiceNo', e.target.value)}
                    placeholder="Optional"
                  />
                </div>
              </div>
            </section>

            <section className="inv-card mypadding">
              <div className="inv-form-grid">
                <div className="inv-field" style={{ gridColumn: '1 / -1' }}>
                  <label>Notes</label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={e => set('notes', e.target.value)}
                    placeholder="Any notes about this purchase..."
                  />
                </div>
              </div>
            </section>

            <section className="inv-card mypadding borderbottom">
              <div className="inv-stats-row" style={{ marginBottom: 20 }}>
                <div className="inv-stat-item">
                  <span className="inv-stat-label">Cost per unit</span>
                  <span className="inv-stat-value">{costPerUnit}</span>
                </div>
                <div className="inv-stat-item">
                  <span className="inv-stat-label">Quantity</span>
                  <span className="inv-stat-value">{form.quantity || '0'} {form.unit}</span>
                </div>
                <div className="inv-stat-item">
                  <span className="inv-stat-label">Total</span>
                  <span className="inv-stat-value">{formatMoney(form.totalCost || 0, preferredCurrency, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>
              <button type="submit" className="inv-btn inv-btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Purchase'}
              </button>
            </section>
          </form>
        </div>
      <Footer />
      </div>
    </>
  );
}
